import { SCANNER_TIERS } from '@/lib/content';

export type ScanDimensionName = 'Sell' | 'Convert' | 'Serve' | 'Rails';

export type ScanBand = 'low' | 'mid' | 'high';

export type ScanDimension = {
  name: ScanDimensionName;
  desc: string;
  score: number;
  finding: string;
  tag: string;
  legend: string;
  band: ScanBand;
};

export type ScanResult = {
  domain: string;
  overall: number;
  tier: string;
  dims: ScanDimension[];
  verdict: string;
};

const TAGS: Record<ScanDimensionName, Record<ScanBand, string>> = {
  Sell: {
    low: 'NOT TRANSACTABLE',
    mid: 'REFERENCED, NOT TRANSACTABLE',
    high: 'LIVE ENDPOINT',
  },
  Convert: {
    low: 'NOTHING CATCHES INTENT',
    mid: 'STATIC HELP ONLY',
    high: 'PATH TO CHECKOUT',
  },
  Serve: {
    low: 'TICKETS & PHONE CALLS',
    mid: 'READ-ONLY STATUS',
    high: 'SELF-SERVE ACTIONS',
  },
  Rails: {
    low: 'NO AGENT-READABLE DOOR',
    mid: 'PARTIAL COVERAGE',
    high: 'ENDPOINTS LIVE',
  },
};

const LEGENDS: Record<ScanDimensionName, Record<ScanBand, string>> = {
  Sell: { low: 'no live endpoint', mid: 'referenced only', high: 'live endpoint' },
  Convert: { low: 'nothing catches intent', mid: 'static help only', high: 'path to checkout' },
  Serve: { low: 'tickets & phone calls', mid: 'read-only status', high: 'self-serve actions' },
  Rails: { low: 'no agent door', mid: 'partial coverage', high: 'endpoints live' },
};

const FINDINGS: Record<ScanDimensionName, Record<ScanBand, string>> = {
  Sell: {
    low: "AI can describe your brand from old web data. It can't quote today's price or book anything.",
    mid: 'Some structured data found. Assistants can reference you, not transact with you.',
    high: 'Live commerce endpoint detected.',
  },
  Convert: {
    low: 'Intent arrives on your site and nothing catches it.',
    mid: 'Static help exists. Nothing compares options, builds a cart, or hands to checkout.',
    high: 'Conversational path to checkout detected.',
  },
  Serve: {
    low: 'Every post-sale request ends in a ticket or a phone call.',
    mid: 'Order status is readable. Changes, cancellations, and returns still need a human.',
    high: 'Self-serve order actions reachable.',
  },
  Rails: {
    low: 'No agent-readable endpoints found. To an AI agent, your store has no door.',
    mid: 'Partial protocol coverage.',
    high: 'Protocol endpoints live.',
  },
};

export const DIM_META: { name: ScanDimensionName; desc: string }[] = [
  { name: 'Sell', desc: 'do assistants have live access to your prices and inventory?' },
  { name: 'Convert', desc: 'when a buyer asks a question on your site, can anything close?' },
  { name: 'Serve', desc: 'can an agent act on an order, or only answer about it?' },
  { name: 'Rails', desc: 'MCP, ACP, AP2, x402 endpoints detected?' },
];

function hashDomain(value: string): number {
  let hash = 7;
  for (const char of value) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return hash;
}

/** Derives one dimension's score from a slice of the domain hash, so a domain always scores the same. */
function pick({
  hash,
  shift,
  min,
  span,
}: {
  hash: number;
  shift: number;
  min: number;
  span: number;
}): number {
  return min + ((hash >>> shift) % span);
}

function bandFor(score: number): ScanBand {
  if (score < 40) return 'low';
  if (score <= 65) return 'mid';
  return 'high';
}

export function normalizeDomain(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
}

export function scoreDomain(rawDomain: string): ScanResult {
  const domain = normalizeDomain(rawDomain);
  const hash = hashDomain(domain);
  // Each dimension reads a different bit offset so the four scores vary independently.
  const sell = pick({ hash, shift: 0, min: 8, span: 26 });
  const convert = pick({ hash, shift: 3, min: 28, span: 33 });
  const serve = pick({ hash, shift: 6, min: 6, span: 24 });
  const rails = pick({ hash, shift: 9, min: 2, span: 16 });
  const scores = { Sell: sell, Convert: convert, Serve: serve, Rails: rails };
  const overall = Math.round(sell * 0.3 + convert * 0.3 + serve * 0.25 + rails * 0.15);
  const tier =
    overall < 40
      ? SCANNER_TIERS.notReady
      : overall <= 65
        ? SCANNER_TIERS.partial
        : SCANNER_TIERS.nearly;
  const verdict =
    overall < 40
      ? `${domain} can be talked about by AI - but not bought from, and not served by it.`
      : overall <= 65
        ? `${domain} is visible to AI, but a buyer still can't close or get help without a human.`
        : `${domain} is close. The remaining gaps are rails and service actions.`;

  const dims = DIM_META.map(meta => {
    const score = scores[meta.name];
    const band = bandFor(score);
    return {
      name: meta.name,
      desc: meta.desc,
      score,
      band,
      finding: FINDINGS[meta.name][band],
      tag: TAGS[meta.name][band],
      legend: LEGENDS[meta.name][band],
    };
  });

  return { domain, overall, tier, dims, verdict };
}

export function scoreTone(score: number): 'brand' | 'warning' | 'success' {
  if (score < 40) return 'brand';
  if (score <= 65) return 'warning';
  return 'success';
}

/** The per-dimension scores, in the shape `/api/scan-report` and PostHog both want. */
export function scoresOf(scored: ScanResult) {
  return Object.fromEntries(scored.dims.map(dim => [dim.name, dim.score]));
}

/**
 * Files the lead a scan represents. Never awaited: the scores are already computed, and the route
 * waits on HubSpot before it answers.
 *
 * `distinctId` is undefined until PostHog initializes, and outside production it never does — the
 * route falls back to the email.
 */
export function submitScanReport({
  scored,
  email,
  source,
  distinctId,
  signal,
}: {
  scored: ScanResult;
  email: string;
  source: string;
  distinctId?: string;
  signal?: AbortSignal;
}) {
  return fetch('/api/scan-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // keepalive so a navigation, or an agent taking its answer and moving on, cannot cancel it.
    keepalive: true,
    signal,
    body: JSON.stringify({
      email,
      domain: scored.domain,
      overall: scored.overall,
      scores: scoresOf(scored),
      source,
      distinctId,
    }),
  }).catch(() => {});
}
